/**
 * „Kanalpreis wirklich ändern?“ (Board „Kanalpreis bestätigen“): Dialog am Desktop, Blatt am Handy;
 * Werte nur aus der Vorschau des Servers.
 */
import type { ChannelPriceModifierPreview } from '@/shared/api';
import {
  BottomSheet,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components';
import { useMediaQuery } from '@/shared/hooks/useMediaQuery';
import { formatMoney } from '@/shared/lib/format';

const INTRO =
  'Die Änderung geht direkt als Auftrag an Channex; Channex führt danach intern einen Full Sync aus. Bis zur Bestätigung ist die Karte gesperrt.';

export function ChannelModifierConfirm(props: {
  preview: ChannelPriceModifierPreview;
  currency: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const mobile = useMediaQuery('(max-width: 767px)');
  const { preview, currency } = props;
  const facts = [
    ['Kanal', preview.title],
    [
      'Bisheriger → neuer Aufschlag',
      `${preview.currentIncreasePercent ?? 0} % → ${preview.draftIncreasePercent ?? 0} %`,
    ],
    [
      'Beispiel Basis → Kanalpreis',
      `${formatMoney(preview.exampleBaseRate, currency)} → ${formatMoney(preview.exampleDerivedRate, currency)}`,
    ],
  ];
  const list = (
    <dl className="flex flex-col">
      {facts.map(([label, value]) => (
        <div
          key={label}
          className="border-card-alt flex min-h-[34px] items-center gap-2.5 border-t text-sm"
        >
          <dt className="text-ink-soft grow">{label}</dt>
          <dd className="font-bold tabular-nums">{value}</dd>
        </div>
      ))}
    </dl>
  );
  const actions = (
    <div className="flex flex-wrap justify-end gap-2">
      <Button
        type="button"
        variant="secondary"
        className="min-h-12 grow md:grow-0"
        onClick={props.onCancel}
      >
        Abbrechen
      </Button>
      <Button
        type="button"
        variant="primary"
        className="min-h-12 grow md:grow-0"
        autoFocus
        onClick={props.onConfirm}
      >
        Änderung beauftragen
      </Button>
    </div>
  );
  const close = (open: boolean) => {
    if (!open) props.onCancel();
  };
  if (mobile) {
    return (
      <BottomSheet
        open
        onOpenChange={close}
        title="Kanalpreis wirklich ändern?"
        description={INTRO}
        footer={actions}
      >
        {list}
      </BottomSheet>
    );
  }
  return (
    <Dialog open onOpenChange={close}>
      <DialogContent showClose={false} className="rounded-hero max-w-[480px]">
        <DialogHeader className="border-b-0">
          <DialogTitle className="font-serif text-2xl font-bold">
            Kanalpreis wirklich ändern?
          </DialogTitle>
          <DialogDescription>{INTRO}</DialogDescription>
        </DialogHeader>
        <div className="px-6 pb-2">{list}</div>
        <DialogFooter>{actions}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
